import { createAsyncThunk } from "@reduxjs/toolkit";

import {
  type ClientTrainingProgramDetails,
  type ProgramWorkout,
} from "@fitness-app/data-models/entities/TrainingProgram";
import { type WorkoutTemplate } from "@fitness-app/data-models/entities/WorkoutTemplate";
import { addFromTemplate } from "@fitness-app/utils/src/programs/workoutsTemplate";

import { type AsyncThunkCreator } from "../../../index";
import { updateWorkoutsInProgram } from "../reducer";
import { getCurrentTraineeProgram } from "../selectors/getCurrentProgram";
import { TRAINEE_PROGRAM_REDUCER_NAME } from "../types";

type Payload = {
  programId: string;
  workoutIdToReplace: string;
  workoutTemplate: WorkoutTemplate | ProgramWorkout;
};

export const replaceWorkoutInProgram = createAsyncThunk<
  ClientTrainingProgramDetails,
  Payload,
  AsyncThunkCreator<string>
>(
  `${TRAINEE_PROGRAM_REDUCER_NAME}/replaceWorkoutInProgram`,
  async (
    { programId, workoutTemplate, workoutIdToReplace },
    { rejectWithValue, getState, dispatch, extra: { db } },
  ) => {
    const currentProgram = getCurrentTraineeProgram(getState());

    const newWorkout = addFromTemplate(workoutTemplate);

    const updatedWorkouts = currentProgram.details.workouts.map((workout) =>
      workout.id === workoutIdToReplace ? { ...newWorkout, id: workoutIdToReplace } : workout,
    );

    dispatch(updateWorkoutsInProgram({ workouts: updatedWorkouts, programId }));

    const { data: programDetails, error } = await db
      .from("client_training_program_details")
      .update({
        workouts: updatedWorkouts,
        updatedAt: new Date().toISOString(),
      })
      .eq("programId", programId)
      .select()
      .single<ClientTrainingProgramDetails>();

    if (error) {
      return rejectWithValue(error.message);
    }

    return programDetails;
  },
);
